import ImageCard from "./ImageCard";
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import type { IMedia } from "../../../model/unityObject";
import { IsLoading, useLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState } from "../../../common/state/slice/appStateSlice";
import {
  getIsFilteredMediasLoading,
  getMediaSearch,
  setCurrentMediaGallery,
} from "../state/gallerySlice";
import Spinner from "../../../common/components/Loader/Spinner";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { menuItems } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import { languageConfig } from "../../../common/util/langConfig";
import { getCurrentUser } from "../../profile/state/profileSlice";
import type { ILanguage } from "../../../model/model";
import { openConfirmationModal, openSlider } from "../../../common/state/slice/modal/modalSlice";
import ModeListProjectGroup, {
  type ProjectGroupListItem,
} from "../../../pages/ProjectGroups/ModeListProjectAndProjectsGroup";
import { NoMediaPlaceholder } from "./NoMediaPlaceholder";
import { UPLOAD_SLIDEOVER_COMPONENT_ID } from "./UploadPanel";
import ModalConfirmationToDeleteMedia from "src/common/components/Modal/ModalConfirmationToDeleteMedia";
import EmptyState from "src/features/statsNew/components/EmptyState";

export const MEDIA_CHECK_BEFORE_DELETE_ID = "rCheckBeforeDelete";
export const MEDIA_MULTIPLE_MEDIA_DELETE_COMPONENT_ID = "deleteMultipleMedias";

const isMediaListItem = (item: ProjectGroupListItem): item is IMedia => "filename" in item;

interface IMediaList {
  medias: Array<IMedia>;
  openMediaDetail: (mediaId: number, item: unknown, index: number) => void;
  currentMedia?: number;
  isMediaPickerMode: boolean;
  customMenuItems?: menuItems;
  className?: string;
  isModalEdit?: boolean;
  listMode?: boolean;
  setListMode?(bool: boolean): () => void;
  componentId: string;
}

export const MEDIA_COMPONENT_ID = "mediaComponentId";

export const MediaList = ({
  medias,
  openMediaDetail,
  currentMedia,
  isMediaPickerMode,
  customMenuItems,
  isModalEdit = true,
  listMode = false,
  componentId,
}: IMediaList) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [idMedia, setIdMedia] = useState(-1);
  const [mediasToDelete, setMediasToDelete] = useState<IMedia[]>([]);
  const [mediasIdToDeleteThatIsChecked, setMediasIdToDeleteThatIsChecked] = useState<
    Array<string | number>
  >([]);
  const currentMediaSearch = useAppSelector(getMediaSearch);
  const isFilteredMediasLoading = useAppSelector(getIsFilteredMediasLoading);
  const isMediaFilterActive =
    (currentMediaSearch.query ?? "").trim().length > 0 ||
    currentMediaSearch.mediaType !== "all" ||
    currentMediaSearch.owner !== "all" ||
    currentMediaSearch.media360tag.trim().length > 0;
  const mediaListLoadingState = useLoading(componentId);
  const hasLoadedMediasOnce = mediaListLoadingState === LoadingState.Success;
  const currentUser = useAppSelector(getCurrentUser);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const [isCheck, setIsCheck] = useState<Array<string>>([]);
  const [isCheckMedias, setIsCheckMedias] = useState<Array<ProjectGroupListItem>>([]);
  const handleClick = (e: ChangeEvent<HTMLInputElement>) => {
    const mediaValue = JSON.parse(e.target.value) as IMedia;

    const { id, checked } = e.target;

    setIsCheck([...isCheck, String(id)]);
    setIsCheckMedias([...isCheckMedias, mediaValue]);

    if (!checked) {
      setIsCheckMedias(isCheckMedias.filter((item) => Number(item.id) !== Number(mediaValue.id)));
      setIsCheck(isCheck.filter((item) => String(item) !== String(id)));
    }
  };

  useEffect(() => {
    setIsCheck([]);
  }, [currentMediaSearch]);

  useEffect(() => {
    setMediasToDelete(isCheckMedias.filter(isMediaListItem));
  }, [isCheckMedias]);

  useEffect(() => {
    setMediasIdToDeleteThatIsChecked(isCheck);
  }, [isCheck]);

  const openModalDeleteMultipleMedias = () => {
    setMediasToDelete(isCheckMedias.filter(isMediaListItem));
    setMediasIdToDeleteThatIsChecked(isCheck);
    dispatch(openConfirmationModal("deleteMultipleMedia"));
  };

  const onDeleteMedia = (media: IMedia) => {
    if (media?.id) {
      setIdMedia(media.id);
      setMediasToDelete([media]);
      setMediasIdToDeleteThatIsChecked([media.id]);
    }
    dispatch(openConfirmationModal("deleteMultipleMedia"));
  };

  return (
    <div
      className={`${
        listMode ? "overflow-y-hidden" : "overflow-y-auto overflow-x-hidden"
      } personalize-scroll-visible h-full flex w-full`}
    >
      <div className="flex personalize-scroll-visible h-full w-full">
        {listMode ? (
          <IsLoading componentId={MEDIA_COMPONENT_ID} showSuccess={false}>
            <IsLoading componentId={MEDIA_CHECK_BEFORE_DELETE_ID} showSuccess={false}>
              <div className="flex h-full w-full">
                <ModeListProjectGroup
                  onDeleteMedia={onDeleteMedia}
                  isModalEdit={isModalEdit}
                  openMediaDetail={openMediaDetail}
                  isMediaList
                  openModalDeleteMultipleMedias={openModalDeleteMultipleMedias}
                  isCheckMedias={isCheckMedias}
                  currentLocale={currentLocale}
                  setIsCheckMedias={setIsCheckMedias}
                  setIsCheck={setIsCheck}
                  isCheck={isCheck}
                  arrayItemsForList={medias}
                  setClickedProjectGroupId={setIdMedia}
                  handleClick={handleClick}
                  clickedProjectGroupId={idMedia}
                  hidePaginationControls
                />
              </div>
            </IsLoading>
          </IsLoading>
        ) : (
          <div
            className={`p-6 grid grid-cols-1 w-full ${
              medias.length > 0
                ? "lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 md:gap-10 h-fit mb-1"
                : "grid-cols-none"
            } `}
          >
            {medias.length === 0 &&
              (isFilteredMediasLoading ? (
                <Spinner relative />
              ) : isMediaFilterActive ? (
                <NoMediaPlaceholder />
              ) : hasLoadedMediasOnce ? (
                <EmptyState
                  icon="folder"
                  title={t("onboarding.emptyStates.medias.title")}
                  description={t("onboarding.emptyStates.medias.description")}
                  action={{
                    label: t("onboarding.emptyStates.medias.cta"),
                    onClick: () => dispatch(openSlider(UPLOAD_SLIDEOVER_COMPONENT_ID)),
                  }}
                />
              ) : null)}
            {medias.map((media, key) => {
              if (!media.organization) {
                console.error("WOWOWOWOWOWOWOWOW !!! !media.organization media: ", media);
              }
              return (
                <ImageCard
                  handleCardClick={() => dispatch(setCurrentMediaGallery(media.id))}
                  key={key}
                  media={media}
                  handleButtonClick={openMediaDetail}
                  currentMedia={currentMedia}
                  isMediaPickerMode={isMediaPickerMode}
                  customMenuItems={customMenuItems}
                  showThreeDotsMenu={false}
                  showEditButton={Number(media.organization?.id) !== 3}
                  id={
                    Number(media.id) === Number(currentMedia) ? "gallery-selected-media-target" : ""
                  }
                />
              );
            })}
          </div>
        )}
      </div>
      {currentUser?.role?.name === "Creator" && (
        <ModalConfirmationToDeleteMedia
          isMediaList
          id={"deleteMultipleMedia"}
          mediasIdToDeleteThatIsChecked={mediasIdToDeleteThatIsChecked}
          mediasToDelete={mediasToDelete}
          idMedia={idMedia}
        />
      )}
    </div>
  );
};
